<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { AccordionContent, AccordionHeader, AccordionItem, AccordionRoot, AccordionTrigger } from 'reka-ui'
import { useQuery } from '@tanstack/vue-query'
import StandardView from '@/standards/StandardView.vue'
import { getValidStandardSets } from '@/standards/api'
import { panelHeaderTextForStandard } from '@/standards/standard_panel_header_text'
import type { StandardDetail } from '@/standards/standard_types'
import { standardEditLink } from '@/goals/goal_standards'
import { GOAL_STANDARDS_EVENT_NAMES } from '@/goals/event_names'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'

const props = withDefaults(
  defineProps<{
    standards?: StandardDetail[]
    isEditLinkShown?: boolean
    isFirstStandardOpen?: boolean
  }>(),
  {
    standards: () => [],
    isEditLinkShown: false,
    isFirstStandardOpen: true
  }
)

const analytics = getAnalyticsTracker()

const openStandardLongRef = ref<string | undefined>(undefined)

const { data: pathwaysStandardSets } = useQuery({
  queryKey: ['validStandardSets'],
  queryFn: getValidStandardSets,
  enabled: computed(() => props.isEditLinkShown),
  staleTime: Infinity
})

watch(
  () => props.standards.map((standard) => standard.standard_long_ref),
  (longRefs, previousLongRefs) => {
    const firstLongRef = longRefs[0]
    const isFirstStandardChanged = firstLongRef !== previousLongRefs?.[0]
    const isOpenStandardGone = longRefs.every((longRef) => longRef !== openStandardLongRef.value)
    if (props.isFirstStandardOpen && firstLongRef && (isFirstStandardChanged || isOpenStandardGone)) {
      openStandardLongRef.value = firstLongRef
    }
  },
  { immediate: true }
)

function editLinkFor(standard: StandardDetail): string {
  if (!props.isEditLinkShown || !pathwaysStandardSets.value) {
    return ''
  }
  return standardEditLink(standard, pathwaysStandardSets.value)
}

function sendToggledEvent(standard: StandardDetail) {
  analytics.sendEvent(GOAL_STANDARDS_EVENT_NAMES.CLICKED_STANDARD_ACCORDION, {
    standard_long_ref: standard.standard_long_ref
  })
}
</script>

<template>
  <AccordionRoot v-model="openStandardLongRef" type="single" :collapsible="true" class="mb-5" data-testid="standards-accordion">
    <AccordionItem
      v-for="standard in standards"
      :key="standard.standard_long_ref"
      :value="standard.standard_long_ref"
      class="mt-1 rounded-xs border border-gray-600 first:mt-0"
    >
      <AccordionHeader as="div">
        <AccordionTrigger
          :title="panelHeaderTextForStandard(standard)"
          class="block w-full truncate rounded-xs px-4 py-2.5 text-left text-md text-blue-400 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400"
          @click="sendToggledEvent(standard)"
        >
          <span v-html="panelHeaderTextForStandard(standard)" />
        </AccordionTrigger>
      </AccordionHeader>
      <AccordionContent>
        <StandardView class="border-t border-gray-300 p-4" :standard="standard" :edit-link="editLinkFor(standard)" />
      </AccordionContent>
    </AccordionItem>
  </AccordionRoot>
</template>
